import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { useHistory, useParams } from 'react-router-dom';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { sdk } from 'src/utilities/sdk';
import { IError } from 'src/utilities/shared-types';
import { WelcomeBackModal } from 'src/components/selfService/features/Signup/components/WelcomeBackModal/WelcomeBackModal';
import { ISignupStep } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTracker';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { getWindow } from 'src/utilities/window';
import { SignupErrorDialog } from 'src/components/selfService/features/Signup/components/SignupErrorDialog/SignupErrorDialog';
import { SignupErrorDialogVariant } from 'src/components/selfService/features/Signup/components/SignupErrorDialog/signupErrorDialog.constants';

import { BusinessDetailsStep } from 'src/components/selfService/features/Signup/steps/BusinessDetailsStep/BusinessDetailsStep';
import { ValidationStep } from 'src/components/selfService/features/Signup/steps/ValidationStep/ValidationStep';
import { LineOfCreditStep } from 'src/components/selfService/features/Signup/steps/LineOfCreditStep/LineOfCreditStep';
import { PeopleStep } from 'src/components/selfService/features/Signup/steps/PeopleStep/PeopleStep';
import { ConfirmationStep } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationStep';
import { useGetQueryParams } from 'src/utilities/useGetQueryParams';
import {
  BalanceSignupStatusContextFields,
  BalanceSignupStatusFieldsResponseRaw,
  BalanceValidationStatus,
} from 'src/components/selfService/common/Balance/balanceSignup.types';
import { transformBalanceSignupStatusFields } from 'src/components/selfService/common/Balance/utils/balanceSignupApiTransformers';

export enum SignupStep {
  COMPANY = 'COMPANY',
  VALIDATION = 'VALIDATION',
  BUDGET = 'BUDGET',
  PEOPLE = 'PEOPLE',
  SUMMARY = 'SUMMARY',
}

export interface ISignupStatusContext {
  activeStep: SignupStep;
  currentStepNumber: number;
  isStepCompleted: (stepName: SignupStep) => boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  getStepNumber: (stepId: SignupStep) => number;
  sendBackButtonAnalytics: () => void;
  corpId: string;
  isWelcomeBackModalPresent: boolean;
  isAdminIncludedInBudget: boolean;
  balance: BalanceSignupStatusContextFields;
}

const SignupStatusContext = createContext<ISignupStatusContext>(null);

export const STEP_MAPPING = {
  1: SignupStep.COMPANY,
  2: SignupStep.VALIDATION,
  3: SignupStep.BUDGET,
  4: SignupStep.PEOPLE,
  5: SignupStep.SUMMARY,
} as const;

export const NUMBER_OF_STEPS = Object.keys(STEP_MAPPING).length;

export const STEP_CONTENT = {
  [SignupStep.COMPANY]: <BusinessDetailsStep />,
  [SignupStep.VALIDATION]: <ValidationStep />,
  [SignupStep.BUDGET]: <LineOfCreditStep />,
  [SignupStep.PEOPLE]: <PeopleStep />,
  [SignupStep.SUMMARY]: <ConfirmationStep />,
};

const SIGNUP_STEPS: ISignupStep[] = [
  {
    name: 'Business profile',
    id: SignupStep.COMPANY,
    content: STEP_CONTENT[SignupStep.COMPANY],
  },
  {
    name: 'Financial information',
    id: SignupStep.VALIDATION,
    content: STEP_CONTENT[SignupStep.VALIDATION],
  },
  {
    name: 'Meal credits',
    id: SignupStep.BUDGET,
    content: STEP_CONTENT[SignupStep.BUDGET],
  },
  {
    name: 'People',
    id: SignupStep.PEOPLE,
    content: STEP_CONTENT[SignupStep.PEOPLE],
  },
  {
    name: 'Review',
    id: SignupStep.SUMMARY,
    content: STEP_CONTENT[SignupStep.SUMMARY],
  },
];

export function getSignupSteps(removeStepContent?: boolean): ISignupStep[] {
  if (removeStepContent) {
    return SIGNUP_STEPS.map(({ name, id }) => {
      return {
        name,
        id,
      };
    });
  }

  return SIGNUP_STEPS;
}

export type SignupStatusResponseRawObject = {
  completed_steps: SignupStep[];
  current_step: SignupStep;
  date_created: string;
  date_modified: string;
  diner_id: string;
  owner_id: string;
  admin_included_in_budget_on_sign_up_complete: boolean;
} & BalanceSignupStatusFieldsResponseRaw;

export interface ISignupStatusResponseRaw {
  object: SignupStatusResponseRawObject;
  status: string;
  validation_errors: IError[];
}

interface ISignupStatus {
  ownerId?: string;
  dinerId?: string;
  step?: SignupStep;
  completedSteps?: SignupStep[];
  currentStepNumber?: number;
  isAdminIncludedInBudget?: boolean;
  balance?: BalanceSignupStatusContextFields;
}
const DEFAULT_STEP_NUMBER = 1;

interface ISignupStatusProviderProps {
  children: ReactNode;
}

function SignupStatusProvider({
  children,
}: ISignupStatusProviderProps): JSX.Element {
  const { stepNumber: urlStepNumber } = useParams<{ stepNumber: string }>();
  const { push, replace } = useHistory();
  const isUpdating = useGetQueryParams('isUpdating');

  const { user, corpId, checkUserAuthentication } = useAuth();
  const { sendTealiumEvent } = useAnalytics();
  const currentUser = useRef(null);
  const [signupStatus, setSignupStatus] = useState<ISignupStatus>({});
  const [showWelcomeBackModal, setShowWelcomeBackModal] = useState(false);
  const [showSignupErrorDialog, setShowSignupErrorDialog] = useState(false);
  const hasClosedWelcomeModal = useRef(false);
  const isFirstAccountCreationAttempt = useRef(false);
  const window = getWindow();

  async function getSignupStatus() {
    try {
      const statusResponse: ISignupStatusResponseRaw = await sdk.http.get(
        `/corp-admin/corp/self/management/${user.ud_id}`
      );

      const { object: statusObject } = statusResponse || {};

      if (statusObject) {
        const {
          completed_steps,
          diner_id,
          owner_id,
          current_step,
          admin_included_in_budget_on_sign_up_complete,
        } = statusObject;

        let currentStepNumber = validateStepNumber(Number(urlStepNumber));
        const backendStepNumber = findCurrentStepNumber(current_step);

        // Balance validation status guard: redirect to Validation step if
        // status has regressed from APPROVED
        const validationStepNumber = findCurrentStepNumber(
          SignupStep.VALIDATION
        );
        const balanceValidationStatus = statusObject.balance_validation_status;
        if (
          balanceValidationStatus &&
          balanceValidationStatus !== BalanceValidationStatus.APPROVED &&
          currentStepNumber > validationStepNumber
        ) {
          currentStepNumber = validationStepNumber;
          replace(
            `/corporate/signup/step/${currentStepNumber}${window.location.search}`
          );
        } else if (
          !(
            hasClosedWelcomeModal.current ||
            isFirstAccountCreationAttempt.current
          )
        ) {
          currentStepNumber = backendStepNumber;

          if (currentStepNumber !== Number(urlStepNumber)) {
            replace(
              `/corporate/signup/step/${currentStepNumber}${window.location.search}`
            );
          }

          // show welcome modal for a returning user with signup in-progress only
          if (completed_steps.length > 0) {
            showWelcomeModal();
          }
        } else if (currentStepNumber > backendStepNumber) {
          currentStepNumber = backendStepNumber;
          replace(
            `/corporate/signup/step/${currentStepNumber}${window.location.search}`
          );
        }

        setSignupStatus({
          ownerId: owner_id,
          dinerId: diner_id,
          step: STEP_MAPPING[currentStepNumber],
          completedSteps: completed_steps,
          currentStepNumber,
          isAdminIncludedInBudget: admin_included_in_budget_on_sign_up_complete,
          balance: transformBalanceSignupStatusFields(statusObject),
        });
      } else {
        // First time there is no signup status, let's take this as an indicator user is not returning with an in-progress status
        isFirstAccountCreationAttempt.current = true;
        // if user has no current signup status, set default values
        setSignupStatus({
          dinerId: user.ud_id,
          step: STEP_MAPPING[DEFAULT_STEP_NUMBER],
          completedSteps: [],
          currentStepNumber: DEFAULT_STEP_NUMBER,
          balance: transformBalanceSignupStatusFields(),
        });

        if (Number(urlStepNumber) !== DEFAULT_STEP_NUMBER) {
          replace(`/corporate/signup/step/${DEFAULT_STEP_NUMBER}`);
        }
      }
    } catch (error) {
      setShowSignupErrorDialog(true);
    }
  }

  function findCurrentStepNumber(currentStep: SignupStep): number {
    for (let i = 1; i <= NUMBER_OF_STEPS; i += 1) {
      if (currentStep === STEP_MAPPING[i]) {
        return i;
      }
    }

    return DEFAULT_STEP_NUMBER;
  }

  function validateStepNumber(stepNumber: number): number {
    if (stepNumber >= 1 && stepNumber <= NUMBER_OF_STEPS) {
      return stepNumber;
    }

    // default step will be step 1 if something invalid is given
    // TODO should this be an error state or redirect elsewhere?
    return DEFAULT_STEP_NUMBER;
  }

  async function goToNextStep() {
    if (isUpdating) {
      // go back to the last step (Confirmation) no matter what
      // if updating a step by coming from confirmation step
      push(`/corporate/signup/step/${NUMBER_OF_STEPS}`);
    } else {
      const { currentStepNumber } = signupStatus;

      const nextStepNumber = currentStepNumber + 1;

      const validationStepNumber = findCurrentStepNumber(SignupStep.VALIDATION);
      const budgetStepNumber = findCurrentStepNumber(SignupStep.BUDGET);
      const peopleStepNumber = findCurrentStepNumber(SignupStep.PEOPLE);

      if (nextStepNumber <= NUMBER_OF_STEPS) {
        if (currentStepNumber === 1) {
          // First time corp creation needs to refresh auth before going to next step
          await sdk.rest.auth.refresh();
          // Force checking credentials in order to set corp ID
          checkUserAuthentication();
        }

        if (
          [validationStepNumber, budgetStepNumber, peopleStepNumber].includes(
            currentStepNumber
          )
        ) {
          await notifyStepCompletion(currentStepNumber);
        }

        push(`/corporate/signup/step/${nextStepNumber}`);
      }
    }
  }

  async function notifyStepCompletion(currentStepNumber: number) {
    try {
      await sdk.http.post(
        `/corp-admin/corp/self/management/${user.ud_id}/${corpId}/${STEP_MAPPING[currentStepNumber]}`,
        {}
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(error);
    }
  }

  function goToPreviousStep() {
    const { currentStepNumber } = signupStatus;

    const previousStepNumber = currentStepNumber - 1;

    if (previousStepNumber >= 1) {
      push(`/corporate/signup/step/${previousStepNumber}`);
    }
  }

  function isStepCompleted(stepId: SignupStep) {
    const completedSteps = get(signupStatus, 'completedSteps') || [];

    return completedSteps.some((step) => step === stepId);
  }

  function showWelcomeModal() {
    setShowWelcomeBackModal(true);
  }

  function closeWelcomeModal() {
    setShowWelcomeBackModal(false);

    if (!hasClosedWelcomeModal.current) {
      hasClosedWelcomeModal.current = true;
    }
  }

  function sendBackButtonAnalytics() {
    sendTealiumEvent({
      eventcategory: 'self-service sign up_all pages',
      eventaction: 'back_cta',
      eventlabel: '',
    });
  }

  useEffect(() => {
    if (
      get(user, 'ud_id') &&
      currentUser.current !== user.ud_id &&
      urlStepNumber
    ) {
      currentUser.current = user.ud_id;
      getSignupStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, urlStepNumber]);

  useEffect(() => {
    if (currentUser.current && urlStepNumber) {
      getSignupStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStepNumber, currentUser]);

  if (!signupStatus.currentStepNumber && !showSignupErrorDialog) {
    return null;
  }

  return (
    <SignupStatusContext.Provider
      value={{
        activeStep: signupStatus.step,
        isStepCompleted,
        goToNextStep,
        goToPreviousStep,
        getStepNumber: findCurrentStepNumber,
        sendBackButtonAnalytics,
        currentStepNumber: signupStatus.currentStepNumber,
        corpId,
        isWelcomeBackModalPresent: showWelcomeBackModal,
        isAdminIncludedInBudget: signupStatus.isAdminIncludedInBudget,
        balance: signupStatus.balance ?? transformBalanceSignupStatusFields(),
      }}
    >
      {children}
      {showWelcomeBackModal && <WelcomeBackModal onClose={closeWelcomeModal} />}
      {showSignupErrorDialog && (
        <SignupErrorDialog
          variant={SignupErrorDialogVariant.API_UNAVAILABLE}
          onDismiss={() => setShowSignupErrorDialog(false)}
        />
      )}
    </SignupStatusContext.Provider>
  );
}

const useSignupStatus = (): ISignupStatusContext => {
  const context = useContext(SignupStatusContext);

  if (!context) {
    throw new Error(
      'useSignupStatus must be used within a SignupStatusProvider'
    );
  }
  return context;
};

export { SignupStatusProvider, useSignupStatus };
